import React, { FC, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { PERFORMANCE } from "../../DevConstants";

interface AdaptiveShadowsProps {
  targetFPS?: number;
  onShadowQualityChange: (shadowMapSize: number[]) => void;
}

/**
 * Component that monitors performance and adjusts shadow quality accordingly.
 * This component must be used inside the Canvas component.
 */
export const AdaptiveShadows: FC<AdaptiveShadowsProps> = ({
  targetFPS = PERFORMANCE.TARGET_FPS,
  onShadowQualityChange,
}) => {
  const frameRates = useRef<number[]>([]);
  const lastFrameTime = useRef<number>(performance.now());
  const [currentQuality, setCurrentQuality] = useState<number[]>(PERFORMANCE.SHADOWS.MEDIUM);

  // If auto-adjust is disabled, use fixed shadow quality based on performance mode
  React.useEffect(() => {
    if (!PERFORMANCE.AUTO_ADJUST) {
      const quality = PERFORMANCE.LOW_PERFORMANCE_MODE ? PERFORMANCE.SHADOWS.LOW : PERFORMANCE.SHADOWS.HIGH;
      setCurrentQuality(quality);
      onShadowQualityChange(quality);
    }
  }, [onShadowQualityChange]);

  // Monitor frame rate and adjust shadow quality
  useFrame(() => {
    if (!PERFORMANCE.AUTO_ADJUST) return;

    const now = performance.now();
    const deltaTime = now - lastFrameTime.current;
    lastFrameTime.current = now;
    
    // Calculate current FPS
    const currentFPS = 1000 / deltaTime;
    
    // Keep track of recent frame rates (last 30 frames)
    frameRates.current.push(currentFPS);
    if (frameRates.current.length > 30) {
      frameRates.current.shift();
    }
    
    // Calculate average FPS
    const avgFPS = frameRates.current.reduce((sum, fps) => sum + fps, 0) / frameRates.current.length;
    
    // Adjust shadow quality based on performance
    let newQuality = currentQuality;
    
    if (avgFPS < targetFPS * 0.8) {
      // If FPS is below 80% of target, reduce shadow quality
      newQuality = PERFORMANCE.SHADOWS.LOW;
    } else if (avgFPS > targetFPS * 1.2) {
      // If FPS is above 120% of target, increase shadow quality
      newQuality = PERFORMANCE.SHADOWS.HIGH;
    } else {
      // If FPS is within acceptable range, use medium quality
      newQuality = PERFORMANCE.SHADOWS.MEDIUM;
    }
    
    // Update shadow quality if changed
    if (newQuality !== currentQuality) {
      setCurrentQuality(newQuality);
      onShadowQualityChange(newQuality);
    }
  });

  // This component doesn't render anything
  return null;
};

/**
 * Utility function to get optimal shadow map size based on performance mode
 */
export const getOptimalShadowMapSize = (
  performanceMode: boolean = PERFORMANCE.LOW_PERFORMANCE_MODE
): number[] => {
  if (performanceMode) {
    return PERFORMANCE.SHADOWS.LOW;
  }
  return PERFORMANCE.SHADOWS.HIGH;
};